// ============================================================================
// LA MODALE — anatomie du panneau de TRAVAIL (formulaire, fiche, parcours).
//
// ⚠️ NE PAS CONFONDRE AVEC `dialog.ts`, qui spécifie le canal BLOQUANT (confirmation, alerte). Deux
// objets, deux plans, deux voiles : une modale de travail se ferme pour aller lire l'écran, un
// dialogue bloquant interrompt tout. Leurs voiles DIFFÈRENT exprès (voir `MODAL_SCRIM`).
//
// SOURCE : relevé des 46 modales de la maquette du back-office (2026-08-16). La maquette fait foi
// (décision utilisateur : « on suit la maquette, la charte évolue pour l'absorber ») ; là où l'on
// s'en écarte, l'écart est écrit avec son motif.
// ============================================================================
import { Z_LAYERS } from './z';

/**
 * LE VOILE de la modale de travail.
 *
 * ⚠️ PLUS OPAQUE ET PLUS FLOU que celui du dialogue bloquant (.55/4 contre .5/3), et ce n'est PAS une
 * incohérence : le bloquant recouvre souvent une modale de travail, et il doit la laisser LIRE
 * pendant qu'on tranche — « supprimer cette grille ? » ne se décide pas sans voir la grille. La
 * modale de travail, elle, n'a rien à laisser lire derrière elle. L'écart entre les deux est un
 * INVARIANT, tenu par un test.
 * ⚠️ Un RÔLE d'encre, jamais un `rgba()` figé : la maquette écrit `rgba(12,14,16,.55)` en dur, donc
 * son voile ne suit pas le thème — posé sur un fond déjà noir, il ne sépare plus rien.
 */
export const MODAL_SCRIM = {
  alpha: 0.55,
  blur: 4,
  /** Marge minimale entre le panneau et le bord de l'écran (valeur dominante : 30 modales sur 46). */
  pad: 28,
  z: Z_LAYERS.modalScrim,
} as const;

/**
 * LARGEURS — sept crans qui absorbent les 25 largeurs relevées, à ±20 px près.
 * ⚠️ AUCUNE LARGEUR LIBRE dans l'API : 436/440/452, puis 470/472/480/490/496/500, font neuf nombres
 * pour deux intentions. Un cran sans nom se recopie — c'est l'histoire de `--r-ctrl` et des 25
 * géométries de pastille.
 */
export const MODAL_WIDTHS = { xs: 440, sm: 480, md: 560, lg: 620, xl: 760, xxl: 880, hero: 920 } as const;
export type ModalWidth = keyof typeof MODAL_WIDTHS;

export const MODAL_CARD = {
  radius: 'lg',
  shadow: 'modal',
  maxWidthVw: 96,
  /**
   * LISERÉ DE TÊTE — 3 px de marque au-dessus de l'en-tête, premier enfant de la carte.
   * ⚠️ Pas décoratif : c'est le SEUL endroit où le panneau touche la couleur du produit, et il lui donne
   * un HAUT quand l'en-tête et le corps sont deux surfaces claires empilées.
   */
  topRule: 3,
  /**
   * ⚠️ FILET AJOUTÉ EN SOMBRE, absent de la maquette. En thème sombre, le panneau et le fond ne sont
   * séparés QUE par l'ombre — et une ombre noire sur un fond noir ne sépare rien. Un panneau dont on
   * ne voit pas le bord n'a plus de forme.
   */
  hairlineDark: 'ink-200',
} as const;

/**
 * HAUTEUR — deux régimes, et le choix se fait sur le PARCOURS, pas sur le goût.
 * `fit` : le panneau se rétracte sur son contenu. Pour une modale à une étape.
 * `cap` : hauteur figée. ⚠️ OBLIGATOIRE dès qu'il y a plusieurs étapes — un pied qui se déplace
 * entre deux étapes fait cliquer « Continuer » à l'endroit où était « Précédent ».
 */
export const MODAL_HEIGHT = { maxVh: 92, caps: { sm: 660, md: 760, lg: 800, xl: 820 } } as const;

/**
 * EN-TÊTE — deux densités, et `fiche` est le PLUS GRAND des deux (relevé 2026-08-17).
 *
 * ⚠️ CE CRAN S'APPELAIT `dense` ET IL ÉTAIT PLUS PETIT : je l'avais déduit (« une fiche, c'est dense »)
 * au lieu de le mesurer. La maquette dit l'inverse — une fiche s'ouvre sur une IDENTITÉ (tuile de 52,
 * titre de 19), un parcours sur une consigne. Renommé pour que son nom cesse de mentir.
 *
 * ⚠️ L'EN-TÊTE EMPILE DEUX RANGÉES : l'identité, puis la barre d'onglets. La seconde n'est pas dans le
 * corps — sinon elle DÉFILE et disparaît au premier coup de molette, alors qu'elle dit où l'on est.
 */
export const MODAL_HEADER = {
  ample: { padV: 18, padH: 22, gap: 13, tile: 38, icon: 19, iconStroke: 1.9, titleSize: 16.5, subSize: 12.5 },
  record: { padV: 18, padH: 22, gap: 14, tile: 52, icon: 24, iconStroke: 1.6, titleSize: 19, subSize: 12 },
  titleWeight: 700,
  titleTracking: '-0.02em',
  tileRadius: 'sm',
  /** ⚠️ LA TUILE EST DU PAPIER BORDÉ, pas un aplat de couleur : seule l'ICÔNE porte la teinte. */
  tileBg: 'paper',
  tileBorder: 'ink-200',
  border: 'ink-200',
  /** Bande de méta sous le titre (code, code-barres, famille, état) — dans l'en-tête FIXE. */
  metaTop: 4, metaGap: 10,
  /** Écart entre la rangée d'identité et la barre d'onglets. */
  tabsTop: 16,
} as const;
export type ModalDensity = 'ample' | 'fiche';

/**
 * TONS de la tuile d'icône — la couleur dit le DOMAINE, jamais l'humeur.
 * (catalogue/prix = teal · import = blue · gamme = violet · réglage à risque = amber · destructif =
 * rose · neutre = ink)
 */
export const MODAL_TILE_TONES = {
  teal: { bg: 'teal-100', fg: 'teal-700' },
  blue: { bg: 'blue-bg', fg: 'blue-fg' },
  violet: { bg: 'violet-bg', fg: 'violet-fg' },
  amber: { bg: 'amber-bg', fg: 'amber-fg' },
  rose: { bg: 'rose-bg', fg: 'rose-fg' },
  /**
   * ⚠️ TON NEUTRE = LE REPLI QUE LA MAQUETTE PRESCRIT quand la famille de l'article est inconnue
   * (encre `ink-600`). Tant qu'aucune table de couleurs de famille n'existe côté back-office, teinter
   * la tuile par la marque affirmerait un classement que rien ne mesure.
   */
  ink: { bg: 'ink-100', fg: 'ink-600' },
} as const;
export type ModalTone = keyof typeof MODAL_TILE_TONES;

/**
 * CORPS — le seul élément qui défile.
 * ⚠️ `minHeight: 0` est STRUCTUREL, pas cosmétique : sans lui, un corps en flex pousse le pied HORS
 * du panneau — et c'est le pied qui porte la validation.
 */
export const MODAL_BODY = { padV: 20, padH: 22 } as const;

/**
 * BANDE FIXE sous l'en-tête (le fil d'étapes d'un assistant) — relevée sur l'import de la maquette.
 * ⚠️ HORS DU CORPS QUI DÉFILE : un fil d'étapes qui défile disparaît au premier coup de molette, exactement quand on
 *    veut savoir où l'on en est.
 */
export const MODAL_BAND = { padV: 14, padH: 22, bg: 'ink-50', border: 'ink-100' } as const;

/** PIED — toujours plus sombre que le corps : il se lit comme une barre, pas comme du contenu. */
export const MODAL_FOOT = {
  padV: 14, padH: 22,
  gap: 10, bg: 'ink-50', border: 'ink-200', actionWeight: 700,
} as const;

/**
 * BOUTON DE FERMETURE — UNE taille.
 * ⚠️ Le second cran a ete SUPPRIME (mesure 2026-08-17) : la fiche, que je croyais « dense », est au cran
 * ample sur ses trois mesures — rembourrage du corps, rembourrage d'en-tete et taille de la croix. Deux
 * crans dont un sans lecteur donnent l'illusion d'une decision.
 */
export const MODAL_CLOSE = {
  size: 34, radius: 'ctrl',
  bg: 'ink-100', fg: 'ink-600', hoverBg: 'ink-200',
  icon: 16, iconStroke: 2.2,
} as const;

/**
 * ANIMATION.
 * ⚠️ `exitMs: 0` est un CONSTAT, pas un choix : la maquette n'a aucune animation de sortie. On ne
 * lui en invente pas une — elle retiendrait 200 ms un panneau que l'utilisateur vient de fermer.
 */
export const MODAL_ANIM = {
  enterMs: 200,
  easing: 'cubic-bezier(.2,.7,.3,1)',
  scaleFrom: 0.96,
  exitMs: 0,
} as const;
